import { buildSettingsRows } from './SettingsRows';
import { playUiClick } from './UiSound';

/** Local achievement data shown on the pause Info screen. */
export interface PauseInfo {
  description: string;
  powers: string[]; // owned power names
  cutscenes: string[]; // seen mid-game cutscenes
  endings: string[]; // seen endings
}

/**
 * Pause overlay, mirroring yknytt's PauseLayer.tscn: a mild darken over the
 * game with a centred panel — the world name, the shared settings rows
 * (mute + theme) and a button row. The Info view (PausePanel.cs:70-78 /
 * InfoScreen/StatPanel) shows the world description + the player's local
 * achievements; the level-server rating/"Achieved N times" counts stay
 * unported (they come from yknytt's online server).
 */
export class PausePanel {
  private el: HTMLDivElement;
  private padRaf = 0;
  private padWasDown = true; // pause button is likely still held from opening

  constructor(
    parent: HTMLElement,
    worldName: string,
    private onResume: () => void,
    private onQuit: () => void,
    onMuteChange: (muted: boolean) => void,
    info?: PauseInfo,
  ) {
    this.el = document.createElement('div');
    this.el.className = 'pause-overlay';
    this.el.innerHTML = `
      <div class="pause-panel">
        <div class="pause-world"></div>
        <div class="pause-settings"></div>
        <div class="pause-info" style="display:none"></div>
        <div class="pause-buttons">
          <button class="knytt-btn" id="resume-btn">Resume</button>
          ${info ? '<button class="knytt-btn" id="info-btn">Info</button>' : ''}
          <button class="knytt-btn" id="quit-btn">Quit to menu</button>
        </div>
      </div>`;
    (this.el.querySelector('.pause-world') as HTMLElement).textContent = worldName;
    (this.el.querySelector('.pause-settings') as HTMLElement).appendChild(
      buildSettingsRows(onMuteChange),
    );
    parent.appendChild(this.el);

    (this.el.querySelector('#resume-btn') as HTMLButtonElement).addEventListener('click', () => {
      playUiClick(); // ui/PausePanel.cs ClickPlayer
      this.resume();
    });
    (this.el.querySelector('#quit-btn') as HTMLButtonElement).addEventListener('click', () => {
      playUiClick();
      this.close();
      this.onQuit();
    });
    if (info) {
      const infoEl = this.el.querySelector('.pause-info') as HTMLElement;
      const section = (label: string, items: string[]): string =>
        items.length
          ? `<div class="pause-info-label">${label}</div><div class="pause-info-items"></div>`
          : '';
      infoEl.innerHTML = `
        <div class="pause-info-desc"></div>
        ${section('Powers:', info.powers)}${section('Cutscenes:', info.cutscenes)}${section('Endings:', info.endings)}`;
      (infoEl.querySelector('.pause-info-desc') as HTMLElement).textContent = info.description;
      const lists = [info.powers, info.cutscenes, info.endings].filter((l) => l.length);
      infoEl.querySelectorAll('.pause-info-items').forEach((el, i) => {
        (el as HTMLElement).textContent = lists[i].join(', ');
      });
      if (!lists.length && !info.description)
        (infoEl.querySelector('.pause-info-desc') as HTMLElement).textContent =
          'No achievements found';
      (this.el.querySelector('#info-btn') as HTMLButtonElement).addEventListener('click', () => {
        playUiClick();
        infoEl.style.display = infoEl.style.display === 'none' ? '' : 'none';
      });
    }
    window.addEventListener('keydown', this.onKey);
    this.padRaf = requestAnimationFrame(this.pollPad);
  }

  // The game loop (and its InputManager) is frozen while paused, so poll the
  // gamepad pause button (L1, yknytt's Joy(4)) here to resume.
  private pollPad = (): void => {
    let down = false;
    for (const pad of navigator.getGamepads?.() ?? []) {
      if (pad?.connected && pad.buttons[4]?.pressed) down = true;
    }
    if (down && !this.padWasDown) {
      this.resume();
      return;
    }
    this.padWasDown = down;
    this.padRaf = requestAnimationFrame(this.pollPad);
  };

  private onKey = (e: KeyboardEvent): void => {
    if (e.code === 'Escape') {
      e.preventDefault();
      this.resume();
    }
  };

  private resume(): void {
    this.close();
    this.onResume();
  }

  private close(): void {
    window.removeEventListener('keydown', this.onKey);
    cancelAnimationFrame(this.padRaf);
    this.el.remove();
  }
}
